import type { FormContext } from "./checks.js";
import { named, type NAME } from "./named.js";

/** Each answered item's response, by item key; an unanswered item is absent. */
export type Answers = Readonly<Record<string, unknown>>;

/**
 * Whether something holds, given the document's answers, such as whether a
 * group or item is asked; like a `Judgement`, it reads time and place only
 * from its context.
 */
export type Condition<Given = Answers> = (args: {
  answers: Given;
  context: FormContext;
}) => boolean;

/** A condition named by `defineRule`, so a decorated form can say which rule decides what it asks, shows or requires. */
export type Rule<Given = Answers> = Condition<Given> & {
  readonly [NAME]: string;
};

/** A rule, named for whoever reads a decorated form. */
export const defineRule = <Given = Answers>(
  name: string,
  condition: Condition<Given>,
): Rule<Given> => named(name, condition);

/**
 * When a group or item is asked: always, never, or as a rule says. `never`
 * is data rather than a rule, so a reader knows it is never asked without
 * running anything.
 */
export type Asked = "always" | "never" | Rule;

/**
 * When an item's comment must be given once it is asked and answered:
 * never, always, or as a rule says. A comment says something about its
 * item's answer, so none is due before there is one.
 */
export type CommentRequired = "never" | "always" | Rule;
